
import React, { useEffect } from 'react';
import { HiringProcess } from '@/components/HiringProcess';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { staffApiClient } from '@/api/staff';
import { logger } from '@/lib/logger';
import { BaseApiClient } from '@/api/baseApiClient';

const HiringPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract the candidate ID from the URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const candidateParam = queryParams.get('candidate');
  const selectedCandidateId = candidateParam ? parseInt(candidateParam) : null;
  
  // Validate candidate ID (it should be a positive integer)
  const isValidCandidateId = selectedCandidateId !== null && 
                             !isNaN(selectedCandidateId) && 
                             selectedCandidateId > 0;
  
  // Prefetch candidate data for better performance
  const { data: candidateData, isLoading } = useQuery({
    queryKey: ['hiring', 'candidate', selectedCandidateId],
    queryFn: async () => {
      if (!isValidCandidateId) return null;
      
      try {
        logger.info({
          message: `Prefetching candidate data for ID: ${selectedCandidateId}`,
        });
        
        // We don't actually need to fetch anything yet, but this enables caching
        // Prefetch any needed data here
        return { id: selectedCandidateId };
      } catch (error) {
        logger.error({
          message: `Error prefetching candidate data for ID: ${selectedCandidateId}`,
          error: error instanceof Error ? error : new Error(String(error))
        });
        return null;
      }
    },
    staleTime: 300000, // 5 minutes
  });
  
  // Prefetch related data to speed up the hiring process
  useEffect(() => {
    if (isValidCandidateId) {
      // Prefetch any other data we might need during the hiring process
      // This helps reduce lag when navigating through the hiring flow
      BaseApiClient.prefetch(
        'hiring:assistantTypes', 
        async () => {
          // Just a placeholder - you could load real assistant types data here
          return { types: ['marketing', 'sales', 'support'] };
        },
        { cache: { enabled: true, duration: 600000 } } // 10 minutes
      );
    }
  }, [isValidCandidateId, selectedCandidateId]);
  
  // If an invalid candidate ID is provided, redirect to staff page
  useEffect(() => {
    if (candidateParam !== null && !isValidCandidateId) {
      logger.warn({
        message: `Invalid candidate ID provided: ${candidateParam}, redirecting to staff page`,
      });
      navigate('/app/staff');
    }
  }, [candidateParam, isValidCandidateId, navigate]);

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <HiringProcess initialCandidateId={isValidCandidateId ? selectedCandidateId : null} />
    </div>
  );
};

export default HiringPage;
